import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  NavigationEnd,
  Router,
  RouterLink,
  type ActivatedRouteSnapshot,
} from '@angular/router';
import {
  AppShellRootComponent,
  injectProtectedEntry,
  injectShellRouteData,
  type ShellHeader,
  type ShellNavEntry,
} from '@sbe-spinoff-client/app-shell';
import { SpinoffAuthService } from '@sbe-spinoff-client/spinoff-platform';
import type { OrgTeamTreeTeam } from '@sbe-spinoff-client/schoolbell-org-team-tree';
import { SidebarContextSelectComponent } from './shared/sidebar-context-select/sidebar-context-select.component';
import {
  catchError,
  filter,
  forkJoin,
  map,
  type Observable,
  of,
  startWith,
  type Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { AccountMenuComponent } from './shared/account-menu.component';
import { appRoutes } from './app.routes';
import { BootGateService } from './shared/boot-gate.service';
import { ContextSwitcherComponent } from './shared/context-switcher/context-switcher.component';
import { ApiService } from './shared/api.service';
import { LogoutService } from './shared/logout.service';
import { ServiceEntryService } from './shared/service-entry/service-entry.service';
import { ServiceListRefreshService } from './shared/service-entry/service-list-refresh.service';
import {
  type Org,
  OrgContextService,
  canManageTeamSettings,
  isTeamOnlyOrg,
} from './shared/org-context.service';
import { ORG_NAV_LABELS } from './features/orgs/shared/org-nav-labels';
import { planSortKeyChanges } from './features/orgs/service-detail/rank-between.util';

type RpServices =
  ReturnType<ApiService['getVisibleServices']> extends Observable<infer T>
    ? T
    : never;

/** 사이드바 서비스 목록의 조회 결과 — 어느 단체·팀 컨텍스트에서 읽은 것인지(`orgId`·`teamId`)를 함께 들고 있어 라우트가 바뀐 직후 낡은 목록을 거른다. */
type RpServicesState = { orgId: string | null; teamId: string | null; services: RpServices };
const EMPTY_RP_SERVICES_STATE: RpServicesState = { orgId: null, teamId: null, services: [] };

/** 현재 라우터 트리에서 가장 깊은 route 파라미터(`orgId`·`teamId`)를 찾는다 — `OrgLayoutComponent.ngOnInit()`
 * (`route.paramMap.subscribe`)이 자기 라우트 레벨에서 하던 걸 앱 루트에서 하려면 트리를
 * 직접 훑어야 한다(`orgId`가 앱 루트 기준으로는 몇 단계 아래 자식에 있으므로). sms/user-web
 * 쪽 `SpinoffActiveOrgContextService.resolveOrgId()`와 동일한 로직 — 이 앱은
 * spinoff-platform의 org 컨텍스트 서비스를 쓰지 않는 별도 인증 스택이라(OrgContextService는
 * "헤더 스위처가 선택한 단체"일 뿐 URL과 무관하게 어긋날 수 있다) 이 앱 안에 그대로
 * 인라인으로 재현한다. */
function resolveRouteParam(router: Router, name: string): string | null {
  let snapshot: ActivatedRouteSnapshot = router.routerState.snapshot.root;
  let value = snapshot.paramMap.get(name);
  while (snapshot.firstChild) {
    snapshot = snapshot.firstChild;
    const local = snapshot.paramMap.get(name);
    if (local) value = local;
  }
  return value;
}

function readRouteParams(router: Router): {
  orgId: string | null;
  teamId: string | null;
} {
  return {
    orgId: resolveRouteParam(router, 'orgId'),
    teamId: resolveRouteParam(router, 'teamId'),
  };
}

interface ShellRouteData extends Record<string, unknown> {
  shellMode: ShellHeader;
  /** true면 <sbe-app-shell> 자체를 건너뛴다(팝업 라우트) — footer도 없음. */
  shellBare: boolean;
}

@Component({
  selector: 'app-root',
  imports: [
    RouterLink,
    MatButtonModule,
    ContextSwitcherComponent,
    AccountMenuComponent,
    AppShellRootComponent,
    SidebarContextSelectComponent,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly auth = inject(SpinoffAuthService);
  private readonly router = inject(Router);
  private readonly logoutSvc = inject(LogoutService);
  private readonly api = inject(ApiService);
  private readonly bootGate = inject(BootGateService);
  private readonly serviceEntry = inject(ServiceEntryService);
  private readonly serviceListRefresh = inject(ServiceListRefreshService);
  private readonly snackBar = inject(MatSnackBar);
  readonly orgContext = inject(OrgContextService);

  /**
   * `dashboard`/`orgs/:orgId`/`login`/`signup`(authGuard·redirectIfLoggedInGuard 보호,
   * app.routes.ts 참고)로 콜드 진입/새로고침 시, 가드가 부팅 시퀀스(auth.init() + 레거시
   * 세션 계승, `BootGateService` 참고)를 기다리는 동안 콘텐츠 영역(헤더·사이드바는 제외)에
   * 로딩 스피너를 표시한다 — `injectSpinoffBootLoading`(sms/timetable/commerce가 쓰는
   * 제네릭 버전, `@sbe-spinoff-client/spinoff-platform`)을 그대로 쓰지 않는 이유는
   * `BootGateService` 참고. `injectProtectedEntry`가 최상위 `appRoutes` 배열만 보고
   * canActivate 유무로 공개/보호 라우트를 나눈다(app.routes.ts가 셸용 최상위 wrapping을
   * 평탄화해 둔 이유이기도 하다) — home·oauth/authorize·member-picker처럼 가드가 없는
   * 화면은 각자 자기 로딩 상태를 보여준다(oauth/authorize는 authorize.component.ts 참고).
   */
  private readonly isProtectedEntry = injectProtectedEntry(appRoutes);
  protected readonly showBootLoading = computed(
    () =>
      this.isProtectedEntry &&
      !this.bootGate.completed() &&
      !this.auth.isAuthenticated(),
  );

  /**
   * 이 앱은 원래부터 홈/로그인/회원가입에도 toolbar(로그인 여부와 무관하게 nav)를
   * 노출해 왔다 — sms-user-web과 달리 셸 없는 랜딩이 존재한 적이 없다. 그래서
   * 기본값을 `'toolbar'`로 유지해 시각 변화를 0으로 둔다. 라우트 데이터 오버라이드는
   * 진짜 팝업(member-picker, RP의 window.open() 대상)에만 `shellBare: true`로 준다.
   * oauth/authorize는 한때 같은 취급이었으나(2026-08-14 실측 정정) 모든 RP가 풀페이지
   * 리다이렉트로만 열어 셸 크롬이 그대로 있어야 자연스럽다 — 일반 라우트로 되돌렸다.
   */
  protected readonly shellRouteData = injectShellRouteData<ShellRouteData>({
    shellMode: 'toolbar',
    shellBare: false,
  });

  readonly userName = computed(() => this.auth.currentUser()?.name ?? null);

  /**
   * `NavigationEnd`마다 재계산되는 현재 route 파라미터 — `resolveRouteParam` 참고. `teamId`는 팀 뷰
   * (`/orgs/:orgId/teams/:teamId`) 안에서만 채워진다. 트리 순회와 구독을 하나로 묶었다.
   */
  private readonly routeParams = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => readRouteParams(this.router)),
    ),
    { initialValue: readRouteParams(this.router) },
  );
  private readonly routeOrgId = computed(() => this.routeParams().orgId);
  private readonly routeTeamId = computed(() => this.routeParams().teamId);

  /**
   * 현재 단체에서 **내가 볼 수 있는** RP 서비스 목록 — 사이드바 「RP 서비스」 섹션의 원천이다. 서버가 노출 판정(사용 결정·팀
   * 숨김·서빙 대상)을 이미 거친 것만 유효 순서로 돌려주므로 화면에서 따로 거르지 않는다. 수락한 구성원 누구나 조회할 수 있어
   * 학생·학부모(team-only)도 쓴다. 403(수락한 자리 없음) 등 실패는 빈 목록으로 수렴해 섹션을 뺀다.
   * 콜드 진입 시 토큰 복원 전에는 호출하지 않고(401로 빈 목록에 고정되는 것을 방지), 인증 완료 시점에
   * 다시 발화하도록 `isAuthenticated`를 트리거에 합성했다. orgId가 바뀌면 switchMap이 이전 요청을 취소해 늦게 도착한 이전 단체 응답이 덮어쓰지 못한다.
   * 또한 `startWith`와 `rpServices` computed의 orgId 일치 검사로, 새 응답 도착 전까지 이전 단체의 서비스가 새 orgId와 조합된 잘못된 메뉴가 노출되지 않게 한다.
   */
  // 조회 키 — 팀 뷰(`teamId` 있음)면 팀 컨텍스트 목록, 아니면 단체 목록이다. `version`은 사용 결정이 바뀐 직후 다시 읽게 하는 신호다.
  // 값이 같으면 같은 키로 보아 NavigationEnd 마다 다시 발화하지 않는다.
  private readonly rpServicesTrigger = computed(
    () => {
      if (!this.auth.isAuthenticated()) return null;
      const orgId = this.routeOrgId();
      return orgId ? { orgId, teamId: this.routeTeamId() ?? null, version: this.serviceListRefresh.version() } : null;
    },
    { equal: (a, b) => a?.orgId === b?.orgId && a?.teamId === b?.teamId && a?.version === b?.version },
  );
  /** 마지막으로 응답을 받은 단체·팀 키 — 같은 키를 **다시 읽을 때**(`serviceListRefresh`)는 이전 목록을 비우지 않아 사이드바가 깜빡이지 않는다. */
  private lastRpServicesKey: string | null = null;
  private readonly rpServicesState = toSignal(
    toObservable(this.rpServicesTrigger).pipe(
      switchMap((trigger) => {
        if (!trigger) {
          this.lastRpServicesKey = null;
          return of(EMPTY_RP_SERVICES_STATE);
        }
        const { orgId, teamId } = trigger;
        const key = `${orgId}|${teamId ?? ''}`;
        const request$ = (teamId ? this.api.getTeamVisibleServices(orgId, teamId) : this.api.getVisibleServices(orgId)).pipe(
          map((services) => ({ orgId: orgId as string | null, teamId, services })),
          catchError(() => of({ orgId: orgId as string | null, teamId, services: [] as RpServices })),
          tap(() => {
            this.lastRpServicesKey = key;
          }),
        );
        // 단체·팀이 바뀐 첫 조회에서만 빈 목록으로 시작한다. 같은 단체·팀의 재조회는 응답이 올 때까지 이전 목록을 그대로 둔다.
        return this.lastRpServicesKey === key
          ? request$
          : request$.pipe(startWith({ orgId: orgId as string | null, teamId, services: [] as RpServices }));
      }),
    ),
    { initialValue: EMPTY_RP_SERVICES_STATE },
  );
  /** `toObservable`은 effect로 값을 흘려 라우트 변경 직후 한 프레임은 이전 단체·팀 데이터가 남는다 —
   * 조회 대상 orgId·teamId 가 현재 라우트와 모두 일치할 때만 노출해 그 갭의 잘못된 메뉴(예: 이전 팀의 서비스)를 동기적으로 막는다. */
  private readonly rpServices = computed(() => {
    const state = this.rpServicesState();
    return state.orgId === this.routeOrgId() && state.teamId === (this.routeTeamId() ?? null) ? state.services : [];
  });

  /**
   * 순서 편집 상태(단체 화면 사이드바, Owner/Admin 전용). `reorderDraft`는 편집 중인 순서(appId 목록)이고, 저장한 직후에는 새 목록이 도착할 때까지
   * 남겨 둬 사이드바가 옛 순서로 잠깐 되돌아가 깜빡이지 않게 한다(`clearDraftAfterRefresh`). 저장 요청은 서비스마다 하나씩이라(단체 행
   * `PUT /orgs/:orgId/apps/:appId/setting`) 바뀐 서비스만 보낸다 — `planSortKeyChanges`.
   */
  private readonly reorderEditing = signal(false);
  private readonly reorderSaving = signal(false);
  private readonly reorderDraft = signal<string[] | null>(null);
  private reorderSub?: Subscription;
  /** 저장 직후 다시 읽는 중인가 — 그때의 목록 상태(`stateAtSave`)와 다른 응답이 오면 초안을 버린다. */
  private awaitingRefresh = false;
  private stateAtSave: RpServicesState | null = null;

  /** 초안 순서대로 정렬한 서비스 목록. 초안에 없는 서비스(그 사이 새로 생김)는 뒤에, 사라진 서비스는 건너뛴다. */
  private orderedByDraft(services: RpServices, draft: readonly string[] | null): RpServices {
    if (!draft) return services;
    const byApp = new Map(services.map((svc) => [svc.appId, svc] as const));
    const ordered = draft.flatMap((appId) => byApp.get(appId) ?? []);
    const rest = services.filter((svc) => !draft.includes(svc.appId));
    return [...ordered, ...rest];
  }

  private startReorder(): void {
    this.reorderDraft.set(this.rpServices().map((svc) => svc.appId));
    this.reorderEditing.set(true);
  }

  private resetReorder(): void {
    this.reorderSub?.unsubscribe();
    this.reorderSub = undefined;
    this.awaitingRefresh = false;
    this.stateAtSave = null;
    this.reorderEditing.set(false);
    this.reorderSaving.set(false);
    this.reorderDraft.set(null);
  }

  private moveReorder(from: number, to: number): void {
    const draft = this.reorderDraft();
    if (!draft || from === to || from < 0 || to < 0 || from >= draft.length || to >= draft.length) return;
    const next = [...draft];
    next.splice(to, 0, ...next.splice(from, 1));
    this.reorderDraft.set(next);
  }

  private saveReorder(orgId: string): void {
    const draft = this.reorderDraft();
    if (!draft || this.reorderSaving()) return;
    const changes = planSortKeyChanges(
      this.rpServices().map((svc) => ({ id: svc.appId, sortKey: svc.sortKey })),
      draft,
    );
    if (!changes) {
      this.snackBar.open('서비스 목록이 바뀌어 순서를 저장하지 못했습니다. 다시 시도해 주세요.', '닫기', { duration: 6000 });
      this.resetReorder();
      return;
    }
    if (changes.size === 0) {
      this.resetReorder();
      return;
    }
    this.reorderSaving.set(true);
    this.reorderSub = forkJoin(
      [...changes].map(([appId, sortKey]) =>
        this.api.putAppLayerSetting(orgId, appId, { sortKey }).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
    ).subscribe((results) => {
      this.reorderSaving.set(false);
      const failed = results.filter((ok) => !ok).length;
      if (failed === results.length) {
        // 하나도 저장되지 않았다 — 편집 중인 순서를 지키고 다시 시도할 수 있게 둔다.
        this.snackBar.open('순서를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.', '닫기', { duration: 6000 });
        return;
      }
      this.reorderEditing.set(false);
      this.awaitingRefresh = true;
      this.stateAtSave = untracked(this.rpServicesState);
      this.serviceListRefresh.refresh();
      if (failed > 0) {
        this.snackBar.open('일부 서비스의 순서만 저장됐습니다. 사이드바 순서를 확인해 주세요.', '닫기', { duration: 6000 });
      }
    });
  }

  /** 서비스 관리(순서 편집·설정 화면) 권한 — 단체 직속 Owner/Admin만. 서버 쓰기 가드도 같은 집합이다(`AppLayerSettingOrgWriteGuard`). */
  private canManageServices(orgId: string): boolean {
    const role = this.orgContext.orgs().find((o) => o.orgId === orgId)?.role;
    return role === 'Owner' || role === 'Admin';
  }

  /**
   * 「RP 서비스」 사이드바 섹션. 항목 클릭은 **서비스 입장**(`ServiceEntryService`)이다 — 관리 화면(`services/:clientId`)이 아니다.
   * 라우트로 이동하지 않고 `action`으로 API를 호출하므로 단체 관리 영역 가드(`orgAdminAreaGuard`) 밖이라 team-only 사용자도
   * 쓸 수 있다. 서비스가 없으면 섹션을 뺀다. 입장 중에는 모든 항목을 막는다(같은 토큰이 두 번 발급되지 않게).
   * 입장은 새 탭에서 연다 — 사이드바를 쓰던 화면을 그대로 두고 여러 서비스를 오가기 위해서다.
   * 단체 화면(`teamId` 없음)에서 Owner/Admin에게는 서비스가 둘 이상일 때 「순서 변경」을 함께 준다(팀 뷰는 단체 순서를 읽기만 한다).
   * 단체 Owner/Admin에게는 항목마다 설정 톱니바퀴(`services/:clientId` 관리 화면 링크)도 준다 — 팀 뷰에서도 같다.
   */
  private rpServicesSection(orgId: string, teamId?: string): ShellNavEntry[] {
    const base = this.rpServices();
    if (base.length === 0) return [];
    const services = this.orderedByDraft(base, this.reorderDraft());
    const disabled = this.serviceEntry.entering() !== null;
    const canManage = this.canManageServices(orgId);
    return [
      {
        heading: 'RP 서비스',
        items: services.map((svc) => ({
          label: svc.name,
          // 팀 뷰에서는 그 팀 컨텍스트로 입장한다(teamId 를 싣는다). 단체 화면에서는 단체 단위 입장이다.
          action: () => this.serviceEntry.enter(orgId, svc, 'toast', teamId, 'new-tab'),
          trailingIcon: 'open_in_new',
          disabled,
          ...(canManage
            ? { secondaryLink: { routerLink: ['/orgs', orgId, 'services', svc.clientId], icon: 'settings', label: `${svc.name} 설정` } }
            : {}),
        })),
        ...(!teamId && base.length >= 2 && canManage
          ? {
              reorder: {
                editing: this.reorderEditing(),
                saving: this.reorderSaving(),
                onStart: () => this.startReorder(),
                onCancel: () => this.resetReorder(),
                onSave: () => this.saveReorder(orgId),
                onMove: (from: number, to: number) => this.moveReorder(from, to),
              },
            }
          : {}),
      },
    ];
  }

  /**
   * 사이드바 맨 위 팀 선택의 원천. 단체 Owner/Admin은 그 단체의 **모든 팀**을 학급구조와 같은 계층으로 보고(`GET /orgs/:orgId/teams`), 그 밖의 사용자는
   * 자기가 직접 속한 팀(`GET /me/orgs`의 teams)만 평평하게 본다. 메뉴를 열 때마다 다시 읽어(`teamsRefresh`) 학급구조에서 방금 만든 팀도 보인다.
   * 조회 결과는 어느 단체의 것인지(`orgId`)를 함께 들고 있어 단체를 옮긴 직후 이전 단체의 팀이 비치지 않는다.
   */
  private readonly teamsRefresh = signal(0);
  private readonly allTeamsTrigger = computed(
    () => {
      const orgId = this.routeOrgId();
      return this.auth.isAuthenticated() && orgId && this.canManageServices(orgId) ? { orgId, version: this.teamsRefresh() } : null;
    },
    { equal: (a, b) => a?.orgId === b?.orgId && a?.version === b?.version },
  );
  private readonly allTeamsState = toSignal(
    toObservable(this.allTeamsTrigger).pipe(
      switchMap((trigger) =>
        trigger
          ? this.api.getOrgTeams(trigger.orgId).pipe(
              map((res) => ({ orgId: trigger.orgId as string | null, teams: res.items as readonly OrgTeamTreeTeam[] })),
              // 실패하면 목록을 비우지 않고 내 팀으로 되돌아간다(아래 `sidebarTeamSelect`).
              catchError(() => of({ orgId: null as string | null, teams: [] as readonly OrgTeamTreeTeam[] })),
            )
          : of({ orgId: null as string | null, teams: [] as readonly OrgTeamTreeTeam[] }),
      ),
    ),
    { initialValue: { orgId: null as string | null, teams: [] as readonly OrgTeamTreeTeam[] } },
  );

  /**
   * 사이드바 맨 위 「단체 / 팀」 선택. 단체에 직접 소속되면 맨 위에 단체 항목이 있고(단체 화면), 팀은 아래에 계층으로 나온다. 팀으로만 연결된 사용자는 단체 항목이
   * 없다(단체 화면에 못 들어간다). 고를 팀이 없으면 선택 자체를 그리지 않는다. 미인증·단체 밖에서는 `null`.
   */
  protected readonly sidebarTeamSelect = computed(() => {
    const orgId = this.routeOrgId();
    if (!orgId || !this.auth.isAuthenticated()) return null;
    const org = this.orgContext.orgs().find((o) => o.orgId === orgId);
    const all = this.allTeamsState();
    const teams: readonly OrgTeamTreeTeam[] =
      all.orgId === orgId
        ? all.teams
        : [
            ...new Map(
              (org?.teams ?? [])
                .filter((t) => t.teamName.trim() !== '')
                .map((t) => [t.teamId, { id: t.teamId, name: t.teamName.trim(), parentPath: [] }] as const),
            ).values(),
          ];
    if (teams.length === 0) return null;
    return {
      orgId,
      orgName: org?.name ?? '단체',
      orgSelectable: !isTeamOnlyOrg(org),
      selectedTeamId: this.routeTeamId(),
      teams,
    };
  });

  /** 사이드바 선택에서 단체(`null`) 또는 팀을 고른 경우의 이동. */
  protected onSidebarContextSelected(orgId: string, teamId: string | null): void {
    void this.router.navigate(teamId ? ['/orgs', orgId, 'teams', teamId] : ['/orgs', orgId]);
  }

  protected onSidebarTeamsOpened(): void {
    this.teamsRefresh.update((v) => v + 1);
  }

  /**
   * org 컨텍스트(`/orgs/:orgId/...`) 안에서만 채워지는 전역 사이드바 항목 — org 밖에서는
   * `[]`이라 셸이 사이드바 자체를 렌더하지 않는다. 라벨·그룹핑은 기존 `OrgLayoutComponent`
   * (`features/orgs/layout/`)와 동일 — 단일 출처 `ORG_NAV_LABELS`를 그대로 재사용한다.
   *
   * `OrgLayoutComponent`는 2026-08-29에 삭제됐다 — `@sbe-spinoff-client/app-shell`이
   * heading/섹션 그룹(`ShellNavSection`)을 지원하게 되어, 이 org 스코프 전용 로컬 2차
   * 사이드바를 전역 사이드바로 완전히 흡수할 수 있게 됐다(sbe-sms/user-web과 동일 결정).
   */
  protected readonly navItems = computed<readonly ShellNavEntry[]>(() => {
    const orgId = this.routeOrgId();
    if (!orgId) return [];
    // `orgs/:orgId/login`(학생 로그인)처럼 단체 id 가 있어도 미인증으로 열리는 화면에서는 단체 사이드바를 그리지 않는다 — 부팅(세션 복원)이 끝났는데도 로그인하지 않았다면 미인증이다.
    // 부팅 중에는 복원될 사용자의 사이드바가 깜빡이지 않게 그대로 둔다. 서비스 목록 조회(`rpServicesTrigger`)도 같은 기준으로 인증 뒤에만 한다.
    if (this.bootGate.completed() && !this.auth.isAuthenticated()) return [];
    const teamId = this.routeTeamId();
    const currentOrg = this.orgContext.orgs().find((o) => o.orgId === orgId);

    // 팀 뷰에서는 단체 관리 메뉴 대신 팀용 메뉴만 둔다 — 팀 구성원에게 단체 관리 화면을 보여 주지
    // 않는다(서버는 각 API가 따로 막는다). 단체 화면으로는 항상 돌아갈 수 있다.
    if (teamId) {
      // team-only 사용자는 단체 화면에 들어갈 수 없다(orgAdminAreaGuard) — 같은 규칙으로 링크를 뺀다.
      const canEnterOrgArea = !isTeamOnlyOrg(currentOrg);
      return [
        {
          label: '팀 홈',
          routerLink: ['/orgs', orgId, 'teams', teamId],
          exact: true,
        },
        ...(canEnterOrgArea
          ? [{ label: '단체 화면', routerLink: ['/orgs', orgId], exact: true }]
          : []),
        ...this.rpServicesSection(orgId, teamId),
        // 단체 뷰의 「학교 설정」과 같은 자리 — 팀 관리자(와 단체 관리자)에게만 보인다.
        ...(canManageTeamSettings(currentOrg)
          ? [
              {
                heading: '팀 설정',
                items: [
                  {
                    label: ORG_NAV_LABELS.teamServiceSettings,
                    routerLink: ['/orgs', orgId, 'teams', teamId, 'service-settings'],
                  },
                ],
              },
            ]
          : []),
      ];
    }

    const path = (segment: string): readonly string[] => [
      '/orgs',
      orgId,
      segment,
    ];
    return [
      {
        label: ORG_NAV_LABELS.dashboard,
        routerLink: ['/orgs', orgId],
        exact: true,
      },
      ...this.rpServicesSection(orgId),
      {
        heading: '학교 설정',
        items: [
          { label: ORG_NAV_LABELS.members, routerLink: path('members') },
          { label: ORG_NAV_LABELS.teams, routerLink: path('teams') },
          {
            label: ORG_NAV_LABELS.teamMembers,
            routerLink: path('team-members'),
          },
          { label: ORG_NAV_LABELS.managedUsers, routerLink: path('managed-users') },
          { label: ORG_NAV_LABELS.labels, routerLink: path('labels') },
          {
            label: ORG_NAV_LABELS.permissions,
            routerLink: path('permissions'),
          },
          { label: ORG_NAV_LABELS.auditLog, routerLink: path('audit-log') },
          { label: ORG_NAV_LABELS.continuity, routerLink: path('continuity') },
        ],
      },
    ];
  });

  constructor() {
    this.bootGate.ensureStarted();

    // 쉘은 로그인 여부와 무관하게 모든 라우트(로그인·회원가입 포함)를 감싸는
    // 최상위 컴포넌트라 route 전환으로는 재생성되지 않는다 — 로그인 직후
    // isLoggedIn()이 뒤늦게 true가 되는 경우도 잡아야 해서 ngOnInit 1회성
    // 체크 대신 effect로 반응한다. ensureLoaded()는 자체적으로 중복 호출을
    // 막는다(loadStarted 플래그).
    //
    // 로그아웃 분기의 orgContext.reset()은 LogoutService.logout()과 별개로도
    // 필요하다 — 토큰 갱신 실패 시 tokenRefreshInterceptor가 LogoutService를
    // 거치지 않고 auth.clearSession()을 직접 호출하는 경로가 있는데, 그 경우
    // reset()이 빠지면 root-singleton인 orgContext가 loadStarted=true로 남아
    // 다음 로그인 사용자에게 이전 사용자의 단체 목록이 그대로 노출된다.
    //
    // shellBare(진짜 팝업 — member-picker)에서는 둘 다 하지 않는다 — 이 effect가 셸
    // 래퍼에 있을 때는 팝업이 애초에 셸 밖 독립 라우트라 이 effect의 영향을 받지 않았다.
    // 셸이 앱 루트로 올라오며 이 effect도 루트로 승격됐는데, 게이팅 없이 그대로 두면
    // 이제 모든 라우트에서 발화해 팝업도 걸린다. ensureLoaded()가 불필요한 getMyOrgs()
    // 호출을 추가하고, reset()은 팝업이 아직 참조 중인 selectedOrgId를 잘못 지울 수 있다.
    // oauth/authorize는 더 이상 shellBare가 아니다(2026-08-14 정정, app.routes.ts 참고) —
    // 이제 toolbar에 app-profile-org-menu가 실제로 뜨므로 ensureLoaded()가 그 화면에서도
    // 정상적으로, 오히려 필요해서 호출된다.
    //
    // 로그인한 채로 **다른 계정으로 로그인**하는 경로도 같은 문제를 만든다 — 학생 코드 로그인 라우트는 이미 로그인한 사용자를 막지 않아(교사가 로그인한 공용 PC에서 학생이
    // 로그인한다) isAuthenticated() 가 계속 true 라 위 분기가 다시 돌지 않는다. 그러면 loadStarted=true 가 남아 학생에게 교사의 단체 목록이 그대로 보인다(로컬 실측,
    // 새로 고침 전까지 유지). 그래서 현재 사용자 id 가 **다른 사용자로** 바뀌면 리셋하고 새 사용자 기준으로 다시 불러온다. 처음 사용자가 확정되는 것(null → 사용자)은 이미
    // 시작한 목록 조회가 같은 사용자의 것이라 리셋하지 않는다.
    let lastUserId: string | null = null;
    effect(() => {
      if (this.shellRouteData().shellBare) return;
      if (!this.auth.isAuthenticated()) {
        lastUserId = null;
        this.orgContext.reset();
        return;
      }
      const userId = this.auth.currentUser()?.id ?? null;
      if (userId !== null) {
        if (lastUserId !== null && userId !== lastUserId) this.orgContext.reset();
        lastUserId = userId;
      }
      this.orgContext.ensureLoaded();
    });

    // 단체나 팀이 바뀌면 이전 컨텍스트 기준으로 진행 중이던 서비스 입장을 취소한다 — 늦게 도착한 응답이 다른 단체 화면에서 RP로
    // 이동시키는 것을 막는다(단체 대시보드가 하던 일을 사이드바 입장이 쓰는 공용 서비스로 옮겼다).
    effect(() => {
      this.routeOrgId();
      this.routeTeamId();
      this.serviceEntry.reset();
      // 편집 중이던 순서도 이전 컨텍스트의 것이라 버린다.
      untracked(() => this.resetReorder());
    });

    // 순서를 저장한 뒤 새 목록이 도착하면 남겨 둔 초안을 버리고 서버 순서를 그대로 보여 준다.
    effect(() => {
      const state = this.rpServicesState();
      untracked(() => {
        if (!this.awaitingRefresh || state === this.stateAtSave) return;
        this.awaitingRefresh = false;
        this.stateAtSave = null;
        this.reorderDraft.set(null);
      });
    });

    // 사이드바 입장은 이 화면 안에 문구를 둘 자리가 없어 실패를 알림으로 알린다. 알림용 신호(`toastError`)는 보여 준 뒤 비워 같은 문구가
    // 다시 나와도 새로 알림이 뜨게 한다. 단체 대시보드의 인라인 문구(`error`)와는 신호를 나눠 이중 표시되지 않고, 사이드바 실패가 같은 단체
    // 대시보드에 낡은 문구로 남지 않는다.
    effect(() => {
      const message = this.serviceEntry.toastError();
      if (!message) return;
      this.snackBar.open(message, '닫기', { duration: 6000 });
      this.serviceEntry.clearToastError();
    });
  }

  // 스위처의 팀 항목 — 팀 뷰로 들어가는 진입점. 선택 단체도 그 팀의 단체로 맞춘다.
  onTeamSelected(selection: { orgId: string; teamId: string }): void {
    this.orgContext.selectOrg(selection.orgId);
    this.router.navigate(['/orgs', selection.orgId, 'teams', selection.teamId]);
  }

  onOrgSelected(org: Org): void {
    this.orgContext.selectOrg(org.orgId);
    this.router.navigate(['/orgs', org.orgId]);
  }

  onCreateOrgRequested(): void {
    this.router.navigate(['/orgs/new']);
  }

  logout(): void {
    void this.logoutSvc.logout();
  }

  /**
   * 인프라 장애 전체 화면에서 "다시 시도" 클릭 시 호출 — 개별 요청을 재현하는 대신
   * 새로고침하는 것이 가장 단순하고 확실하다(sms/commerce user-web과 동일 결정).
   */
  protected retry(): void {
    window.location.reload();
  }
}
